import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Ban, Loader2, Plus, RefreshCw, Trash2, KeyRound } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';
import {
  CredentialHealthLines,
  CredentialStateBadge,
  type CredentialLifecycle,
  getCredentialState,
  isCredentialEnabled,
} from '../../components/credential/CredentialLifecycle';
import type { OrgMember } from './memberTypes';

interface OrgCredential extends CredentialLifecycle {
  id: string;
  label: string;
  accessKey: string;
  venue: string;
  environment: 'prod' | 'demo';
  // Kalshi numbered subaccount (1-63) the key is locked to; null = unrestricted
  // key on the primary account. All enabled keys in the pool share one value.
  subaccount?: number | null;
  /** 'rfq_desk' keys are reserved for one trader's RFQ desk and never join
   *  the pool; `assignedUserId` is that trader. */
  purpose?: 'pool' | 'rfq_desk';
  assignedUserId?: string | null;
  createdAt: string;
}

/** Members a desk can be assigned to: operators, and traders with RFQ on. */
export function deskEligibleMembers(members: readonly OrgMember[]): OrgMember[] {
  return members.filter((m) => m.role !== 'trader' || m.traderRules?.rfqEnabled === true);
}

/** Desk <select> value: '' = org pool, 'desk:' = unassigned desk, 'desk:<id>'. */
function deskValue(c: Pick<OrgCredential, 'purpose' | 'assignedUserId'>): string {
  return c.purpose === 'rfq_desk' ? `desk:${c.assignedUserId ?? ''}` : '';
}

function deskBody(value: string): { purpose: 'pool' | 'rfq_desk'; assignedUserId: string | null } {
  if (!value.startsWith('desk:')) return { purpose: 'pool', assignedUserId: null };
  const id = value.slice('desk:'.length);
  return { purpose: 'rfq_desk', assignedUserId: id === '' ? null : id };
}

interface OrgKeysPanelProps {
  orgQueryParam?: string;
  /** Org members, for the RFQ desk assignee picker. */
  members?: readonly OrgMember[];
  embedded?: boolean;
  className?: string;
}

export function OrgKeysPanel({
  orgQueryParam = '',
  members = [],
  embedded = false,
  className,
}: OrgKeysPanelProps) {
  const [creds, setCreds] = useState<OrgCredential[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed read is not "no keys": it shows a retry, never the empty-state invite.
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [label, setLabel] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [subaccount, setSubaccount] = useState('');
  const [deskFor, setDeskFor] = useState('');
  const [hardDeleteTarget, setHardDeleteTarget] = useState<OrgCredential | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const enabledCount = creds.filter(isCredentialEnabled).length;
  const deskMembers = deskEligibleMembers(members);
  const memberName = (id: string | null | undefined) =>
    members.find((m) => m.id === id)?.username ?? (id ? 'unknown member' : 'unassigned');

  const fetchCreds = useCallback(async () => {
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to load org keys');
        setLoadFailed(true);
        setCreds([]);
      } else {
        // Server returns all venues now (kalshi + polymarket); this panel
        // only renders kalshi rows. Polymarket rows render beside it.
        const all = (data.credentials ?? []) as OrgCredential[];
        setCreds(all.filter((c) => c.venue === 'kalshi'));
      }
    } catch {
      setError('Network error');
      setLoadFailed(true);
      setCreds([]);
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => { fetchCreds(); }, [fetchCreds]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || !accessKey.trim() || !privateKey.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/credentials${orgQueryParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: label.trim(),
          accessKey: accessKey.trim(),
          privateKeyPem: privateKey,
          subaccount: subaccount.trim() === '' ? null : Number(subaccount.trim()),
          ...(deskFor ? deskBody(deskFor) : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Failed to add key');
      } else {
        setCreds((prev) => [...prev, data.credential]);
        setLabel('');
        setAccessKey('');
        setPrivateKey('');
        setSubaccount('');
        setDeskFor('');
        setShowForm(false);
      }
    } catch {
      setError('Network error');
    }
    setSubmitting(false);
  };

  const handlePatchCredential = useCallback(async (
    credential: OrgCredential,
    body: {
      disabled?: boolean;
      release?: boolean;
      purpose?: 'pool' | 'rfq_desk';
      assignedUserId?: string | null;
    },
  ) => {
    setActingId(credential.id);
    setError(null);
    const res = await apiFetch(`/api/manager/credentials/${credential.id}${orgQueryParam}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: OrgCredential;
      error?: string;
    };
    if (res.ok) {
      setCreds((prev) => prev.map((c) => (c.id === credential.id ? data.credential ?? c : c)));
    } else {
      setError(data.error ?? 'Failed to update key');
    }
    setActingId(null);
  }, [orgQueryParam]);

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    const id = hardDeleteTarget.id;
    setHardDeleteTarget(null);
    setActingId(id);
    const res = await apiFetch(credentialUrl(
      `/api/manager/credentials/${id}`,
      orgQueryParam,
      { hard: 'true', confirm: 'delete' },
    ), { method: 'DELETE' });
    if (res.ok) {
      setCreds((prev) => prev.filter((c) => c.id !== id));
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      setError(data.error ?? 'Failed to delete key');
    }
    setActingId(null);
  };

  const Wrapper = embedded ? 'div' : 'section';
  const wrapperClassName = embedded
    ? `min-w-0 ${className ?? ''}`
    : `bg-card border-border rounded-lg border p-4 ${className ?? 'mb-6'}`;

  return (
    <Wrapper className={wrapperClassName}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="flex shrink-0 items-center gap-2">
            <KeyRound size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold">Org Kalshi Keys</h3>
            <span className="text-muted-foreground text-[11px]">
              ({loading ? '…' : loadFailed ? 'unavailable' : creds.length === 0 ? 'none' : `${enabledCount}/${creds.length} enabled`})
            </span>
          </div>
          <div
            className="flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] leading-snug text-amber-200/90 light:text-amber-700"
            title="Org Kalshi keys load into one rotating pool. Requests rotate across enabled keys and fall through when a key is quarantined; private keys are encrypted at rest and never shown again after upload."
          >
            <AlertTriangle size={12} className="shrink-0 text-amber-100 light:text-amber-700" />
            <span>
              <span className="font-medium text-amber-100 light:text-amber-700">Pool safety:</span> only production keys from one
              Kalshi account/subaccount, all write-enabled — no demo, read-only, or other-account keys.
              RFQ desk keys sit outside the pool.
            </span>
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500">
          <Plus size={14} /> {showForm ? 'Cancel' : 'Add Key'}
        </button>
      </div>

      {error && (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="bg-muted/50 border-border mb-3 space-y-2 rounded-md border p-3">
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
              placeholder="e.g. main-key-1" />
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Access Key ID</label>
            <input value={accessKey} onChange={(e) => setAccessKey(e.target.value)}
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none"
              placeholder="kalshi access key id" />
            <p className="text-muted-foreground mt-1 text-[10px]">
              Must belong to the same production Kalshi account/subaccount as the other enabled
              org keys.
            </p>
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">
              Subaccount (optional)
            </label>
            <input value={subaccount} onChange={(e) => setSubaccount(e.target.value)}
              inputMode="numeric"
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 font-mono text-xs focus:border-blue-500 focus:outline-none"
              placeholder="1-63 — only for a subaccount-locked key" />
            <p className="text-muted-foreground mt-1 text-[10px]">
              If this API key is locked to a Kalshi numbered subaccount, enter that number — the
              org then trades only that subaccount&apos;s balance (a hard funds cap). Leave blank
              for an unrestricted key. All enabled keys in the pool must match.
            </p>
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Use</label>
            <select value={deskFor} onChange={(e) => setDeskFor(e.target.value)}
              aria-label="Key use"
              className="bg-card border-border text-foreground w-full rounded-md border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none">
              <option value="">Org pool (dash orders, bots)</option>
              {deskMembers.map((m) => (
                <option key={m.id} value={`desk:${m.id}`}>RFQ desk for {m.username}</option>
              ))}
            </select>
            <p className="text-muted-foreground mt-1 text-[10px]">
              An RFQ desk key never joins the pool: only that trader&apos;s RFQ quotes use it, and
              every fill on it is theirs. It needs a subaccount, which also caps the desk&apos;s money.
            </p>
          </div>
          <div>
            <label className="text-muted-foreground mb-1 block text-[11px]">Private Key (PEM)</label>
            <textarea value={privateKey} onChange={(e) => setPrivateKey(e.target.value)}
              className="bg-card border-border text-foreground h-32 w-full rounded-md border px-2 py-1 font-mono text-[10px] focus:border-blue-500 focus:outline-none"
              placeholder="-----BEGIN RSA PRIVATE KEY-----..." />
          </div>
          <button type="submit" disabled={submitting}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500 disabled:opacity-50">
            {submitting && <Loader2 size={12} className="animate-spin" />} Save Key
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
        </div>
      ) : loadFailed ? (
        <p className="text-muted-foreground py-2 text-center text-xs">
          Couldn't load keys.{' '}
          <button type="button" onClick={() => { void fetchCreds(); }} className="underline underline-offset-2">
            Retry
          </button>
        </p>
      ) : creds.length === 0 ? (
        <p className="text-muted-foreground py-2 text-center text-xs">
          No org keys yet. Add one to enable org-keyed trading for this org.
        </p>
      ) : (
        <div className="space-y-2">
          {creds.map((c) => {
            const state = getCredentialState(c);
            const disabled = state.kind === 'disabled';
            const canRelease =
              !disabled &&
              (!!c.quarantinedUntil ||
                Number(c.consecutiveAuthFailures ?? 0) > 0 ||
                !!c.lastErrorAt);
            const busy = actingId === c.id;
            return (
              <div
                key={c.id}
                className={`border-border/60 bg-muted/30 rounded-md border px-3 py-2 ${
                  disabled ? 'opacity-75' : ''
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                    <span className="text-foreground text-xs font-medium">{c.label}</span>
                    <CredentialStateBadge credential={c} />
                    <span className="bg-secondary text-muted-foreground rounded px-1.5 py-0.5 text-[10px]">
                      {c.environment}
                    </span>
                    {c.subaccount != null && (
                      <span
                        className="rounded bg-purple-500/15 px-1.5 py-0.5 text-[10px] text-purple-300 light:text-purple-700"
                        title={`Key locked to Kalshi subaccount ${c.subaccount} — the org trades only that subaccount's balance`}
                      >
                        sub {c.subaccount}
                      </span>
                    )}
                    {c.purpose === 'rfq_desk' && (
                      <span
                        className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-300 light:text-emerald-700"
                        title="RFQ desk key: outside the org pool; its fills belong to this trader"
                      >
                        RFQ desk · {memberName(c.assignedUserId)}
                      </span>
                    )}
                    <span className="text-muted-foreground/40">·</span>
                    <span className="font-mono">{c.accessKey.slice(0, 10)}...</span>
                    <span className="text-muted-foreground/40">·</span>
                    <span>Added {new Date(c.createdAt).toLocaleDateString()}</span>
                    <CredentialHealthLines credential={c} inline />
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {c.subaccount != null && (
                      <select
                        value={deskValue(c)}
                        disabled={busy}
                        aria-label={`Use of ${c.label}`}
                        title="Org pool, or an RFQ desk reserved for one trader"
                        onChange={(e) => void handlePatchCredential(c, deskBody(e.target.value))}
                        className="bg-card border-border text-foreground rounded border px-1.5 py-0.5 text-[11px] disabled:opacity-50"
                      >
                        <option value="">Org pool</option>
                        {c.purpose === 'rfq_desk' && !deskMembers.some((m) => m.id === c.assignedUserId) && (
                          <option value={deskValue(c)}>RFQ desk · {memberName(c.assignedUserId)}</option>
                        )}
                        {deskMembers.map((m) => (
                          <option key={m.id} value={`desk:${m.id}`}>RFQ desk · {m.username}</option>
                        ))}
                      </select>
                    )}
                    {canRelease && (
                      <button
                        onClick={() => handlePatchCredential(c, { release: true })}
                        disabled={busy}
                        title="Release quarantine and reset auth failure count"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-400 disabled:opacity-50"
                      >
                        <RefreshCw size={12} className={busy ? 'animate-spin' : ''} />
                        Release
                      </button>
                    )}
                    <button
                      onClick={() => handlePatchCredential(c, { disabled: !disabled })}
                      disabled={busy}
                      title={disabled ? 'Enable key' : 'Disable key'}
                      className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      <Ban size={12} />
                      {disabled ? 'Enable' : 'Disable'}
                    </button>
                    {disabled && (
                      <button
                        onClick={() => setHardDeleteTarget(c)}
                        disabled={busy}
                        title="Permanently delete key"
                        className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
                      >
                        <Trash2 size={12} />
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={handleHardDelete}
        title="Permanently Delete Org Key"
        message={`Permanently delete "${hardDeleteTarget?.label}"? This removes the encrypted key material and lifecycle history. Disable is safer unless this key should never be recoverable.`}
        confirmLabel="Delete Permanently"
        variant="danger"
      />
    </Wrapper>
  );
}

function credentialUrl(
  path: string,
  orgQueryParam: string,
  extra: Record<string, string>,
): string {
  const params = new URLSearchParams(orgQueryParam ? orgQueryParam.slice(1) : '');
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
